import { defineStore } from 'pinia';
import { AssessmentRecordFormService } from '@/services';
import { ApiErrorResponse, RecordFormInterface } from '@/types';
import { useAssessmentStore, useMaterialStore } from '@/store';

interface RecordFormStateInterface {
  recordForms: {
    loading: boolean;
    data: RecordFormInterface[];
    total: number;
  };
  error: boolean;
  message: string;
}

export default defineStore('AssessmentRecordFormStore', {
  state: (): RecordFormStateInterface => ({
    recordForms: {
      loading: false,
      data: [],
      total: 0,
    },
    error: false,
    message: '',
  }),

  actions: {
    setRecordForms() {
      const assessmentStore = useAssessmentStore();
      const forms = assessmentStore.currentAssessment.data?.assessment_record_forms || [];

      this.recordForms.data = [...forms].sort((a, b) => a.title.localeCompare(b.title));
      this.recordForms.total = this.recordForms.data.length;
    },

    async createRecordForm(assessmentId: number, payload: { title: string; document: File }) {
      try {
        const formData = new FormData();
        formData.append('title', payload.title);
        formData.append('document', payload.document);

        const response = await AssessmentRecordFormService.createRecordForm(assessmentId, formData);
        await this.refreshAssessmentData(assessmentId);
        return response.data;
      } catch (error) {
        throw error as ApiErrorResponse;
      }
    },

    async updateRecordForm(assessmentId: number, recordFormId: number, payload: { title: string; document?: File }) {
      try {
        const formData = new FormData();
        formData.append('title', payload.title);
        if (payload.document) {
          formData.append('document', payload.document);
        }

        const response = await AssessmentRecordFormService.updateRecordForm(assessmentId, recordFormId, formData);
        await this.refreshAssessmentData(assessmentId);
        return response.data;
      } catch (error) {
        throw error as ApiErrorResponse;
      }
    },

    async deleteRecordForm(id: number, assessmentId: number) {
      try {
        const response = await AssessmentRecordFormService.deleteRecordForm(id);
        await this.refreshAssessmentData(assessmentId);
        return response.data;
      } catch (error) {
        throw error as ApiErrorResponse;
      }
    },

    async refreshAssessmentData(assessmentId: number) {
      const assessmentStore = useAssessmentStore();
      const materialStore = useMaterialStore();

      await assessmentStore.clearCurrentAssessment();
      await assessmentStore.getAssessment(assessmentId);
      this.setRecordForms();
      materialStore.setMaterials();
    },
  },
});
